import './locations.css';

import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { AddressValidation, OrderType, Pages, StoreStatusId, Modals, CookiePreferences, ContentType, StoreListType, MapProvider } from '../../../types/enums';
import { ILocation, ILocationModules, ILocationProps } from './locations.interface';
import { IUserAddress } from '../../../models/user.interface';
import { IAddressFormat, IName } from '../../../interfaces/global.interfaces';
import { ITempAddress } from '../../../interfaces/temp.interface';
import { DialogCreators } from '../../../utils/dialog-creators';
import { OrderTypeSelector } from '../../shared/OrderTypeSelector/order-type-selector';
import { Page } from '../page';
import { Data } from '../../../models/data';
import { ProfileService } from '../../../services/profile-service';
import { OrderService } from '../../../services/order-service';
import { AddressService } from '../../../services/address-service';
import { LocationService } from '../../../services/location-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { DeliveryAddress } from '../../shared/DeliveryAddress/delivery-address';
import { getCountryCentroid } from '../../../services/geo-search/country-centroid';
import { Dialog } from '../../modals/Dialog/dialog';
import { ILocationSelector } from '../../modals/LocationSelector/location-selector.interface';
import { IStoreLocation, IStoreLocationHour } from '../../../models/data.interface';
import { TimedMessage } from '../../shared/TimedMessage/timed-message';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { Router } from '../../../core/router';
import type { SimpleCoords } from "../../../types/geolocation-types";
import { ConfiguredMapProvider } from '../../../services/maps/configured-map-provider';
import { getGeoServiceApiKeys } from '../../../services/geo-service-api-keys';
import { IMapMarker, LatLng } from '../../../services/maps/map-provider.interface';
import { GeoSearchProviderFactory } from '../../../services/geo-search/geo-search-provider-factory';
import { AddressSuggestBox } from '../../shared/AddressSuggestBox/address-suggest-box';
import { MapMarkerInfoWindow } from '../../shared/MapMarkerInfoWindow/map-marker-info-window';
import { ConfirmButtonState } from '../../shared/DeliveryAddress/delivery-address.interface';
import { buildNormalizedAddress, isAddressBlockedAsync, showBlockedAddressError } from '../../shared/DeliveryAddress/address-exception';

export class Locations extends Page {
    private readonly _profileService: ProfileService;
    private readonly _orderService: OrderService;
    private readonly _locationService: LocationService;
    private readonly _addressService: AddressService;
    private readonly _dataLayer: DataLayerService;
    private readonly _item: ILocation;
    private readonly _modules: ILocationModules;
    private _options?: ILocationProps;
    private _orderTypeSelector?: OrderTypeSelector;
    private _didInitDefaultLocation = false;
    private readonly _configuredMapProvider: ConfiguredMapProvider;
    private _selectedStoreList: StoreListType = StoreListType.NEARBY;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, orderService: OrderService,
        locationService: LocationService, addressService: AddressService, dataLayer: DataLayerService) {
        super("page_locations", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._orderService = orderService;
        this._locationService = locationService;
        this._addressService = addressService;
        this._dataLayer = dataLayer;
        this._item = this._initialModel();
        this._configuredMapProvider = new ConfiguredMapProvider(data, context, MapProvider.GOOGLE);
        this._modules = this._initialModules();
        this._eventBusListeners();
    }

    private _initialModel(): ILocation {
        return {
            useSvgIcon: this._data.getProperty("UISettings")?.showSvgLocationIcons || false,
            orderTypeId: this._context.getProperty("defaultOrderType"),
            currentLatitude: 0,
            currentLongitude: 0,
            mapInstance: null,
            defaultMapZoom: 10,
            markers: [],
            selectedStore: "",
            tempAddress: null
        };
    }

    private _initialModules(): ILocationModules {
        return {
            mapProvider: this._configuredMapProvider.provider,
            mapMarkerInfoWindow: new MapMarkerInfoWindow(this._data, this._localizationService)
        };
    }

    private _setMapProvider(): void {
        if (!this._configuredMapProvider.refresh())
            return;

        this._modules.mapProvider = this._configuredMapProvider.provider;
        this._item.mapInstance = null;
    }

    private _setGeocoder(): void {
        if (this._modules.deliveryAddress)
            return;

        const settings = this._data.getBrandProperty("Settings");
        const geoSearch = GeoSearchProviderFactory.createSelection(
            settings.LOCSET,
            settings.OOS.autocompleteProvider,
            getGeoServiceApiKeys(this._data, this._context)
        );

        this._modules.geoSearch = geoSearch;
        this._modules.deliveryAddress = new DeliveryAddress(
            this._data,
            this._orderService.order,
            this._localizationService,
            this._locationService,
            this._addressService,
            this._dataLayer,
            geoSearch,
            "locations",
            false,
            this._deliveryAddressChanged
        );
    }

    public async initAsync(): Promise<void> { }

    public override show() {
        if (this._profileService.user.isLoggedIn())
            this._loggedIn()
        else
            this._loggedOut();
        super.show();

        // Clear input values via Two Way binding listeners
        //    document.querySelectorAll("#page_locations input").forEach(e => e.dispatchEvent(new Event("input")))
        this._newPageRenderAsync().then(() => this._setOptions());

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayer)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
    }

    public override hide() {
        super.hide();
        this._cleanOptions();
    }

    private async _newPageRenderAsync(): Promise<void> {
        const orderTypeId = this._options?.forOrderType || this._orderService.order.getProperty("OrderTypeID") || this._context.getProperty("defaultOrderType");
        await this._orderTypeChangedAsync(orderTypeId);
        this._item.selectedStore = this._data.storeKey;

        Util.hideElement("div_locations_store_list");
        Util.showElement("div_locations_store_list_placeholder");

        await this._renderPageAsync();
        this._setEventListeners();
    }

    private readonly _setOptions = (): void => {
        Util.setElement("innerText", "spn_locations_address_form_header_text", this._localizationService.names("NewAddress")); //default
        Util.setElement("ltag", "spn_locations_address_form_header_text", "NewAddress");

        if (!this._options) return;

        if (this._options?.forOrderType) {
            this._orderTypeSelector && (this._orderTypeSelector.element.style.display = "none");
        }

        if (this._options?.nearbyOnly) {
            const selector = document.getElementById("div_locations_store_list_selector");
            selector && (selector.style.display = "none");
        } else {
            const selector = document.getElementById("div_locations_store_list_selector");
            selector && (selector.style.display = "");
        }

        const map = document.getElementById("div_locations_map_container");

        if (this._options?.hideMap) {
            map && (map.style.display = "none");
            const deliveryList = document.getElementById("div_locations_delivery_list");
            deliveryList?.classList.add("map-hidden");
        } else {
            map && (map.style.display = "");
            const deliveryList = document.getElementById("div_locations_delivery_list");
            deliveryList?.classList.remove("map-hidden");
        }

        if (this._options?.originPage === Pages.ACCOUNT) {
            Util.setElement("innerText", "spn_locations_address_form_header_text", this._localizationService.names("AddNewDeliveryAddress"));
            Util.setElement("ltag", "spn_locations_address_form_header_text", "AddNewDeliveryAddress");

            document.querySelectorAll(".user-address-delete").forEach(e => {
                e.addEventListener("click", this._deleteAddress)
                e.classList.toggle("hidden", false);
            })
        } else {
            document.querySelectorAll(".user-address-delete").forEach(e => {
                e.removeEventListener("click", this._deleteAddress)
                e.classList.toggle("hidden", true);
            })
        }

        if (this._options.nearbyOnly)
            this._selectedStoreList = StoreListType.NEARBY;
        else if (this._options.storeListType)
            this._selectedStoreList = this._options.storeListType;

    }

    private readonly _cleanOptions = (): void => {
        if (this._options?.forOrderType != undefined) {
            this._orderTypeSelector && (this._orderTypeSelector.element.style.display = "");
        }

        if (this._options?.nearbyOnly) {
            const selector = document.getElementById("div_locations_store_list_selector");
            selector && (selector.style.display = "");
        }

        if (this._options?.hideMap) {
            const map = document.getElementById("div_locations_map_container");
            map && (map.style.display = "");
            const deliveryList = document.getElementById("div_locations_delivery_list");
            deliveryList?.classList.remove("map-hidden");
        }

        if (this._options?.originPage === Pages.ACCOUNT) {
            document.querySelectorAll(".user-address-delete").forEach(e => {
                e.removeEventListener("click", this._deleteAddress)
                e.classList.toggle("hidden", true);
            })
        }

        this._options = {};
    }

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED_LOCATIONS, this._orderTypeChangedAsync);
        this._eventBus.on(EventTypes.DELIVERY_ADDRESS_CHANGED, this._deliveryAddressChanged);
        this._eventBus.on(EventTypes.LOGGED_IN, this._loggedIn);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._loggedOut);
        this._eventBus.on(EventTypes.FAV_STORE_REMOVE, (payload: { storeKey: string; isFavoritesPage: boolean }) => { this._removeFavoriteStoreHandler(payload.storeKey, payload.isFavoritesPage) });

        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, async () => {
            this._setMapProvider();
            this._setGeocoder();

            if (Router.path === Pages.LOCATIONS)
                this.show();
        });

        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages, options?: ILocationProps) => {
            if (page == Pages.LOCATIONS) {
                this._options = options;
                this._setOptions();
            }
        });

        this._eventBus.on(EventTypes.LOCATION_SELECTION, async (options?: ILocationSelector) => {
            if (options)
            {
                await this._getLocationFromLocationSelectorAsync(options.countryId.toString(), options.zip);
                this._didInitDefaultLocation = true;
                return;
            }

            if (this._didInitDefaultLocation) return;

            this._didInitDefaultLocation = true;
            await this._initializeMapWithDefaultLocationAsync();
        });

        this._eventBus.on(EventTypes.DATALAYER_LOADED, () => {
            //initial attempt to capture page_view may not have had the dataLayer loaded yet
            if (this._element?.classList.contains("open"))
                this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
        });
    }

    /**
     * Initializes and renders the page by setting up order types and map location.
     * Retrieves the user's location from cookies or geolocation API, or falls back to a default location if needed.
     */
    private async _renderPageAsync(): Promise<void> {
        this._createOrderTypes();
        await this._renderDeliverySectionAsync();

        Util.hideElement("div_locations_store_favorites");
        Util.hideElement("div_locations_store_list");

        // Check if current location is set, otherwise try to load location from cookie or geolocation
        if (this._item.currentLatitude === 0 || this._item.currentLongitude === 0) {
            const cookieLocation = Util.getUserLocationFromCookie();

            if (cookieLocation)
                await this._initializeMapWithStoresAsync(cookieLocation.lat, cookieLocation.lng);
            else
                await this._getLocationFromGeolocationAsync();
        } else {
            // If location is already set, initialize the map with it
            await this._initializeMapWithStoresAsync(this._item.currentLatitude, this._item.currentLongitude);
        }

        if (this._item.orderTypeId === OrderType.DELIVERY) {
            const addressId = this._getCurrentAddressId();
            await this._deliveryBoxOnClickAsync(addressId);
        }

        setTimeout(() => {
            this._renderSelectedStoreList();
        }, 1000);
    }

    private readonly _renderSelectedStoreList = (): void => {
        const hasFavoriteStores = this._renderFavoriteStoreList();

        if (hasFavoriteStores && this._selectedStoreList === StoreListType.FAVORITES)
            this._favoriteListSelectorOnClick();
        else
            this._nearbyListSelectorOnClick();

        Util.hideElement("div_locations_store_list_placeholder");
    }

    private readonly _setEventListeners = (): void => {
        if (this._profileService.user.isLoggedIn())
            Util.setElement("onclick", "div_locations_address_form_header", this._newDeliveryBoxOnClickAsync);

        Util.setElement("onclick", "div_locations_store_list_selector_favorite", this._favoriteListSelectorOnClick);
        Util.setElement("onclick", "div_locations_store_list_selector_nearby", this._nearbyListSelectorOnClick);

        Util.addTabListArrowNavigation(document.getElementById("div_locations_store_list_selector"), tab => tab.click());
        this._setLoggedInEventListeners();
    }

    private readonly _setLoggedInEventListeners = (): void => {
        const userAddresses = this._profileService.user.getProperty("Addresses") || [];
        for (const address of userAddresses) {
            Util.setElement("onclick", `div_locations_delivery_user_address_${address.AID}`, async () => {
                Util.setElementClass("remove", "div_locations_address_form_content", "open");
                Util.setElementClass("remove", "div_locations_address_form_header", "open");
                await this._deliveryBoxOnClickAsync(address.AID);
            })
        }
    }

    private readonly _loggedIn = (): void => {
        //Show selector
        const hasFavoriteStores = this._renderFavoriteStoreList();
        Util.showElement("div_locations_store_list_selector");
        Util.setElementClass("add", "div_locations_store_favorites", "signed-in");
        Util.setElementClass("add", "div_locations_store_list", "signed-in");

        if (hasFavoriteStores && this._selectedStoreList === StoreListType.FAVORITES)
            this._favoriteListSelectorOnClick();
        else
            this._nearbyListSelectorOnClick();
        // show Hearts
        const heartDivs = Array.from(document.querySelectorAll(".favorite-section")) as HTMLDivElement[];
        for (const div of heartDivs) {
            Util.showElement(div);
        }

        const userAddresses = this._profileService.user.getProperty("Addresses") || [];
        if (userAddresses.length == 0) {
            Util.setElementClass("add", "div_locations_delivery_list_new", "active");
            Util.hideElement("spn_locations_address_form_header_icon");
            Util.setElementClass("add", "div_locations_address_form_header", "open");
            Util.setElementClass("add", "div_locations_address_form_content", "open");
            //Remove the onclick event for the open/close on delivery new address because it is sole element.
            const el = document.getElementById('div_locations_address_form_header');
            el && (el.onclick = null);
        } else {
            //Setting onclick again because we remove it on signout.
            Util.setElement("onclick", "div_locations_address_form_header", this._newDeliveryBoxOnClickAsync);
            Util.setElementClass("remove", "div_locations_delivery_list_new", "active");
            Util.showElement("spn_locations_address_form_header_icon");

            if (this._orderService.order.getProperty("Address")) {
                Util.setElementClass("add", "div_locations_address_form_header", "open");
                Util.setElementClass("add", "div_locations_address_form_content", "open");
            } else {
                Util.setElementClass("remove", "div_locations_address_form_header", "open");
                Util.setElementClass("remove", "div_locations_address_form_content", "open");
            }

            // Build user address section
            this._createUserDeliveryAddresses();
        }

        this._setLoggedInEventListeners();
    }

    private readonly _loggedOut = (): void => {
        this._clearStoreListContainer();

        //Hide selector
        Util.hideElement("div_locations_store_list_selector");
        Util.hideElement("div_locations_store_favorites");
        Util.setElementClass("remove", "div_locations_store_favorites", "signed-in");
        Util.showElement("div_locations_store_list");
        Util.setElementClass("remove", "div_locations_store_list", "signed-in");

        const heartDivs = Array.from(document.querySelectorAll(".favorite-section")) as HTMLDivElement[];
        for (const div of heartDivs) {
            Util.hideElement(div);
        }

        // Set New Address as selected
        Util.setElementClass("add", "div_locations_delivery_list_new", "active");
        Util.hideElement("spn_locations_address_form_header_icon");
        Util.setElementClass("add", "div_locations_address_form_header", "open");
        Util.setElementClass("add", "div_locations_address_form_content", "open");
        //Remove the onclick event for the open/close on delivery new address, because they are signed out.
        const el = document.getElementById('div_locations_address_form_header');
        el && (el.onclick = null);
        Util.setElement("innerHTML", "div_locations_delivery_list_user_cards", "");
    }

    private readonly _newDeliveryBoxOnClickAsync = async (): Promise<void> => {
        if (document.getElementById("div_locations_address_form_content")?.classList.contains("open")) {
            Util.setElementClass("remove", "div_locations_address_form_content", "open");
            Util.setElementClass("remove", "div_locations_address_form_header", "open");
        } else {
            Util.setElementClass("add", "div_locations_address_form_content", "open");
            Util.setElementClass("add", "div_locations_address_form_header", "open");
        }
        await this._deliveryBoxOnClickAsync(0);
        this._modules.deliveryAddress?.clearStreetAddress();
    }

    private readonly _deliveryBoxOnClickAsync = async (addressId: number, displayError: boolean = true): Promise<void> => {
        const userAddress = this._profileService.user.getProperty("Addresses")?.find(x => x.AID == addressId) || null;

        if (!userAddress)
            addressId = 0;

        this._clearSelectedAddressClass(addressId);

        if (addressId !== 0) {
            Util.setElementClass("add", `div_locations_delivery_user_address_${addressId}`, "active");
            Util.setElementClass("remove", "div_locations_address_form_content", "open");

            if (userAddress)
                await this._modules.deliveryAddress?.checkDeliveryAddressAsync(userAddress, displayError);
        } else {
            this._deliveryAddressChanged(AddressValidation.INVALID, "", null);
        }
    }

    private readonly _favoriteListSelectorOnClick = (): void => {
        this._selectedStoreList = StoreListType.FAVORITES;
        Util.setElementClass("add", "div_locations_store_list_selector_favorite", "active");
        Util.setElementClass("remove", "div_locations_store_list_selector_nearby", "active");
        this._setStoreListTabSelected(StoreListType.FAVORITES);

        Util.showElement("div_locations_store_favorites");
        Util.hideElement("div_locations_store_list");
    }

    private readonly _nearbyListSelectorOnClick = (): void => {
        this._selectedStoreList = StoreListType.NEARBY;
        Util.setElementClass("add", "div_locations_store_list_selector_nearby", "active");
        Util.setElementClass("remove", "div_locations_store_list_selector_favorite", "active");
        this._setStoreListTabSelected(StoreListType.NEARBY);

        Util.hideElement("div_locations_store_favorites");
        Util.showElement("div_locations_store_list");
    }

    private readonly _setStoreListTabSelected = (selected: StoreListType): void => {
        document.getElementById("div_locations_store_list_selector_nearby")
            ?.setAttribute("aria-selected", String(selected === StoreListType.NEARBY));
        document.getElementById("div_locations_store_list_selector_favorite")
            ?.setAttribute("aria-selected", String(selected === StoreListType.FAVORITES));
        document.getElementById("div_locations_store_list_selector_nearby")
            ?.setAttribute("tabindex", selected === StoreListType.NEARBY ? "0" : "-1");
        document.getElementById("div_locations_store_list_selector_favorite")
            ?.setAttribute("tabindex", selected === StoreListType.FAVORITES ? "0" : "-1");
    }


    private readonly _clearSelectedAddressClass = (addressId: number | null): void => {
        const allAddresses = Array.from(document.querySelectorAll(".user-address")) as HTMLDivElement[];
        for (const address of allAddresses) {
            address.classList.remove("active");
        }

        const div = document.querySelector(".address-form");
        if (div) {
            if (addressId == null || addressId == 0)
                div.classList.add("active");
            else
                div.classList.remove("active");
        }
    }

    private readonly _createOrderTypes = (): void => {
        const parentDiv = document.getElementById("div_locations_order_types");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        Util.showElement("div_locations_order_types");

        this._orderTypeSelector = new OrderTypeSelector(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            this._dataLayer,
            this._orderService.order,
            this._profileService.loyaltyProvider,
            this._data.getBrandProperty("OrderTypeSubTypes"),
            this._item.orderTypeId,
        );
        parentDiv.appendChild(this._orderTypeSelector.element);

        Util.hideElement("div_locations_order_types_placeholder");
    }

    private async _getLocationFromLocationSelectorAsync(countryId: string, zipCode: string): Promise<void> {
        const countryName = this._data.getBrandProperty("Countries").find(x => x.CountryID == Number(countryId))?.Name || 'United States of America';

        const addressQuery = zipCode && zipCode.trim().length > 0
            ? `${zipCode}, ${countryName}`
            : countryName;

        const coords = await this._modules.geoSearch?.chain?.geocodeAsync(addressQuery).catch(() => null);

        if (coords) {
            this._saveLocationToCookie(coords.lat, coords.lng);
            await this._initializeMapWithStoresAsync(coords.lat, coords.lng);
            return;
        }

        console.error("Geocoding failed for:", addressQuery);
        await this._initializeMapWithDefaultLocationAsync();
    }

    /**
     * Attempts to get the user's current location via the browser's geolocation API.
     * If successful, saves the location in a cookie and initializes the map.
     */
    private readonly _getLocationFromGeolocationAsync = async (): Promise<void> => {
        const pos: SimpleCoords | null = await Util.getCurrentPositionAsync(5000);

        if (!pos) {
            // Failure: user blocked, error, or timeout
            this._initializeLocationSelector();
            return;
        }

        this._saveLocationToCookie(pos.latitude, pos.longitude);
        await this._initializeMapWithStoresAsync(pos.latitude, pos.longitude);
    }

    /**
     * Saves the user's location in a cookie for future access if the user's cookie preference is "accepts".
     * 
     * @param latitude - The user's latitude.
     * @param longitude - The user's longitude.
     */
    private readonly _saveLocationToCookie = (latitude: number, longitude: number): void => {
        if (!Util.getCookiePreferenceFromCookie() || Util.getCookiePreferenceFromCookie() === CookiePreferences.REJECT)
            return;

        const locationData = JSON.stringify({ lat: latitude, lng: longitude });
        const domain = window.location.hostname.split('.').reverse().slice(0, 2).reverse().join('.');
        document.cookie = `locationData=${locationData};max-age=3600;path=/;domain=${domain}`;
    }

    /**
     * Initializes the location selector modal to allow the user to input their location if the user's current location has not yet been determined.
     */
    private _initializeLocationSelector(): void {
        if (this._item.currentLatitude === 0 || this._item.currentLongitude === 0) {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOCATION_SELECTOR);
            this._eventBus.emit(EventTypes.LOCATION_SELECTION);
        }
    }

    /**
     * Initializes the map with a default location based on the user's country code.
     * 
     * @param countryCode - Optional ISO country code (e.g., "US") to determine the default location.
     */
    private readonly _initializeMapWithDefaultLocationAsync = async (countryCode?: string): Promise<void> => {
        const defaultLocation = this._getDefaultUserCoordinates(countryCode);
        await this._initializeMapWithStoresAsync(defaultLocation.lat, defaultLocation.lng);
    }

    private readonly _orderTypeChangedAsync = async (orderTypeId: OrderType): Promise<void> => {
        const refreshMarkers = (this._item.markers.length === 0 && this._data.locations.length > 0) || (this._item.markers.length > 0 && this._data.locations.length > 0 && this._item.orderTypeId !== orderTypeId);

        this._item.orderTypeId = orderTypeId;
        if (this._item.mapInstance) {
            await this._createMapMarkersAndMarkerClusterAsync(refreshMarkers);
            await this._renderStoreListAsync();
            this._renderFavoriteStoreList();
            this._mapShowLocations();
        }

        if (orderTypeId !== OrderType.DELIVERY) {
            Util.hideElement("div_locations_delivery_list");
            Util.showElement("div_locations_non_delivery_list");
            Util.setElementClass("remove", "div_main_locations", "delivery");
        } else {
            const addressId = this._getCurrentAddressId();
            await this._deliveryBoxOnClickAsync(addressId, false);
            Util.showElement("div_locations_delivery_list");
            Util.hideElement("div_locations_non_delivery_list");
            Util.setElementClass("add", "div_main_locations", "delivery");
        }

        this._setLoggedInEventListeners();
    }

    private _getCurrentAddressId(): number {
        const address = this._orderService.order.getProperty("Address");
        if (address)
            return this._orderService.order.getProperty("AddressID");

        const primaryUserAddress = (this._profileService.user.getProperty("Addresses") || []).find(x => x.IPRM);
        return (primaryUserAddress ? primaryUserAddress.AID : 0);
    }

    private async _renderDeliverySectionAsync(): Promise<void> {
        if (this._profileService.user.isLoggedIn())
            this._createUserDeliveryAddresses();

        await this._createDeliveryAddressComponentAsync();
    }

    private readonly _createUserDeliveryAddresses = (): void => {
        const addresses = this._profileService.user.getProperty("Addresses") || [];

        const listElement = document.getElementById("div_locations_delivery_list_user_cards");
        listElement && (listElement.innerHTML = "");
        
        for (const address of addresses) {
            const card = this._userAddressCard(address);
            listElement?.appendChild(card);
        }
    }

    private readonly _userAddressCard = (address: IUserAddress): HTMLElement => {
        const tempAddress: IAddressFormat = {
            StreetNo: address.STRNO,
            Address1: address.ADDR1,
            Address2: address.ADDR2,
            Address3: address.ADDR3,
            Address4: address.ADDR4,
            Address5: address.ADDR5,
            City: address.CITY,
            State: address.STA,
            ZipCode: address.ZIP,
            CountryID: address.CID || 1,
            AddressTypeID: address.ATID,
        };

        const addressObject = Util.formatAddressObject(tempAddress, this._data.getBrandProperty("Countries"));
        const html = `
            <div id="div_locations_delivery_user_address_${address.AID}" class="user-address">
                <div class="user-address-info">
                    <h4>${addressObject.address1}${addressObject.address2},</h4>
                    ${addressObject.address3 ? `<p>${addressObject.address3}</p>` : ""}
                    <p>${addressObject.cityState}</p>
                </div>
                <div id="div_locations_delivery_user_address_delete_${address.AID}" class="user-address-delete hidden" data-address=${address.AID}>${this._localizationService.names("Delete")}</div>
            </div>`;

        const htmlElement = Util.createHtmlElementFromTemplate(html);
        const addressStartOrderButton = this._createAddressCardStartOrderButton(address, htmlElement);
        htmlElement.appendChild(addressStartOrderButton);

        return htmlElement;
    }

    private readonly _createAddressCardStartOrderButton = (userAddress: IUserAddress, div: HTMLElement): HTMLButtonElement => {
        const button = document.createElement("button");

        if (this._options?.isSave) {
            button.textContent = this._localizationService.names("SelectAddress");
            button.setAttribute("ltag", "SelectAddress");

        } else {
            button.textContent = this._localizationService.names("StartOrder");
            button.setAttribute("ltag", "StartOrder");
        }

        button.onclick = async (event) => {
            event.stopPropagation();

            Util.setElementClass("remove", "div_locations_address_form_content", "open");
            Util.setElementClass("remove", "div_locations_address_form_header", "open");

            await this._deliveryBoxOnClickAsync(userAddress.AID);

            const addressInfo = this._modules.deliveryAddress?.getDeliveryAddressInfo();

            if (addressInfo?.address && addressInfo?.storeKey) {
                this._startOrderOnClickAsync();
            }
        };
        return button;
    };


    private async _createDeliveryAddressComponentAsync(): Promise<void> {
        const parentDiv = document.getElementById("div_locations_address_form_content_parent");
        if (!parentDiv || !this._modules.deliveryAddress)
            return;

        parentDiv.innerHTML = "";

        parentDiv.appendChild(this._modules.deliveryAddress.element);
        await this._modules.deliveryAddress.initAsync([...this._data.locations]);
        this._modules.deliveryAddress.setConfirmButton(
            this._options?.isSave ? "SelectAddress" : "StartOrder",
            this._startOrderOnClickAsync
        );

        if (this._orderService.order.getProperty("AddressID") === 0)
            Util.setElementClass("add", "div_locations_address_form_content", "open");
    }

    private readonly _deliveryAddressChanged = (status: AddressValidation, storeKey: string, address: ITempAddress | null): void => {
        switch (status) {
            case AddressValidation.VALID:
                this._deliveryAddressChangedSuccess(storeKey, address);
                break;
            case AddressValidation.INVALID:
            case AddressValidation.OUT_OF_DELIVERY_ZONE:
            case AddressValidation.OUT_OF_TIME:
            case AddressValidation.INVALID_NO_ZIP:
            case AddressValidation.BLOCKED:
            case AddressValidation.INVALID_ST_NUMBER:
            case AddressValidation.INVALID_ST_NAME:
            case AddressValidation.INVALID_NO_STREET_NUMBER:
            default:
                this._deliveryAddressChangedFailure(address);
                break;
        }
    }

    private readonly _deliveryAddressChangedSuccess = (storeKey: string, address: ITempAddress | null): void => {
        this._item.selectedStore = storeKey;
        this._item.tempAddress = address;

        const store = this._data.locations.find(x => x.storeKey == storeKey);
        if (store)
            this._moveToStoreLocationOnMap(store);
        this._modules.deliveryAddress?.setConfirmButtonState(ConfirmButtonState.ENABLED);
    }

    private readonly _deliveryAddressChangedFailure = (address: ITempAddress | null): void => {
        this._clearSelectedAddressClass(address ? address.AddressID : null);
        this._item.selectedStore = "";
        this._item.tempAddress = null;
        this._orderService.order.setAddress(null);
        this._modules.deliveryAddress?.setConfirmButtonState(ConfirmButtonState.DISABLED);
    }

    /**
     * Initializes the map with the user's position and renders nearby stores.
     * 
     * @param latitude - The user's latitude.
     * @param longitude - The user's longitude.
     */
    private async _initializeMapWithStoresAsync(latitude: number, longitude: number): Promise<void> {
        await this._updateUserPositionAndStoreList(latitude, longitude);
        this._centerMapOnUser();
        if (this._item.mapInstance)
            this._item.mapInstance.setZoom(this._item.defaultMapZoom);
    }

    private readonly _startOrderOnClickAsync = async (): Promise<void> => {
        if (this._item.orderTypeId !== OrderType.DELIVERY && this._item.selectedStore) {
            const selectedStore = this._data.locations.find(x => x.storeKey == this._item.selectedStore);
            if (selectedStore) {
                this._validateStoreDistance(selectedStore, this._executeStoreSelection, this._resetStoreSelection);
                return;
            }
        }

        await this._executeStoreSelection();
    }

    private readonly _validateStoreDistance = (store: IStoreLocation, onContinue: () => void, onCancel: () => void): void => {
        if (store.distance <= 5 || this._options?.forSettingPreferredStore) {
            onContinue();
            return;
        }

        this._showDistanceWarningDialog(store, onContinue, onCancel);
    }

    private readonly _showDistanceWarningDialog = (
        store: IStoreLocation,
        onContinue: () => void,
        onCancel: () => void
    ): void => {
        const addressObj = this._formatAddress(store);
        const storeAddress = `${addressObj.address1} ${addressObj.cityState}`;

        let distance: string = this._createDistanceText(store.distance).text;
        distance = `<span class="highlight">${distance}</span>`;

        const dineInTextNames = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === OrderType.DINE_IN)?.Names;
        const dineInText = this._localizationService.GetName(dineInTextNames ?? []);

        const takeOutTextNames = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === OrderType.TAKE_OUT)?.Names;
        const takeOutText = this._localizationService.GetName(takeOutTextNames ?? []);

        const orderType: string = this._item.orderTypeId === OrderType.DINE_IN
            ? dineInText
            : takeOutText;

        const dialogMessage: string = this._localizationService
            .names("StoreDistanceDialogMessage")
            .replace("{{order_type}}", orderType)
            .replace("{{store_address}}", storeAddress)
            .replace("{{store_distance}}", distance);

            const noButtonText = this._localizationService.names("No");
            const yesButtonText = this._localizationService.names("Yes");

        DialogCreators.messageBox(
            dialogMessage,
            [
                { text: noButtonText, callBack: onCancel },
                { text: yesButtonText, callBack: onContinue },
            ]
        );
    }

    private readonly _executeStoreSelection = async (): Promise<void> => {
        const previousStoreKey = this._data.storeKey;
        let tempAddress: ITempAddress | null = null;

        if (this._item.orderTypeId === OrderType.DELIVERY) {
            const addressInfo = this._modules.deliveryAddress!.getDeliveryAddressInfo();

            const isAddressBlocked = await this._IsAddressBlocked(addressInfo);

            if (isAddressBlocked) {
                this._showBlockedAddressError(addressInfo.address!);
                this._deliveryAddressChanged(AddressValidation.BLOCKED, "", null);
                return;
            }

            if (addressInfo.storeKey && addressInfo.address?.AddressID === 0) {
                this._item.selectedStore = addressInfo.storeKey;
                this._item.tempAddress = addressInfo.address!;
            }

            if (addressInfo.address)
                tempAddress = addressInfo.address!;
        } else {
            this._orderService.order.setAddress(null);
        }

        this._handleStartOrderDataLayer();

        await this._handleCartItemsOnStoreChange(previousStoreKey, tempAddress);
    }

    private _buildNormalizedAddress(address: ITempAddress): string {
        return buildNormalizedAddress(address);
    }

    private async _IsAddressBlocked(addressInfo: {storeKey: string, address: ITempAddress | null}): Promise<boolean> {
        return await isAddressBlockedAsync(this._orderService, addressInfo.address, addressInfo.storeKey);
    }

    private _showBlockedAddressError(address: ITempAddress): void {
        showBlockedAddressError(this._localizationService, this._dataLayer, address);
    }

    private _handleStartOrderDataLayer(): void {
        if (this._dataLayer) {
            const selectedStore = this._data.locations.find(x => x.storeKey == this._data.storeKey);
            const storeCode = selectedStore?.storeCode ?? "";
            const storeName = selectedStore?.name ?? "";
            this._dataLayer.select_store(storeCode, storeName);

            if (this._options?.forChangingStore) {
                this._dataLayer.change_store(storeCode, storeName);
            }
        }
    }

    private async _handleCartItemsOnStoreChange(previousStoreKey: string, tempAddress: ITempAddress | null): Promise<void> {
        const items = this._orderService.order.getProperty("Items") ?? [];
        if (items.length === 0) {
            this._startOrderContinued(tempAddress);
        } else {
            const result = await this._orderService.order.checkCartOkayOnStoreChangeAsync(this._item.selectedStore, this._profileService.loyaltyProvider);
            if (result.success)
                this._startOrderContinued(tempAddress);
            else
                await this._handleInvalidCartItemsOnStoreChange(result.invalidRecIds, tempAddress, previousStoreKey);
        }
    }

    private async _handleInvalidCartItemsOnStoreChange(invalidRecIds: number[], tempAddress: ITempAddress | null, previousStoreKey: string): Promise<void> {
        let dialogMessage = this._localizationService.names("CART_ITEMS_UNAVAILABLE_STORE_SWITCH");

        const orderItems = this._orderService.order.getProperty("Items");
        const uniqueItemRecIds = new Set(orderItems.map((x) => x.ItemRecId));
        let itemNames = "";

        if (invalidRecIds.length < uniqueItemRecIds.size) {
            const oldStoreData = await this._data.getStoreDataAsync(
                previousStoreKey,
                this._profileService.loyaltyProvider
            );
            for (const recId of invalidRecIds) {
                const orderItem = orderItems.find(x => x.ItemRecId === recId);
                const menuItem = oldStoreData?.Items.find(x => x.ItemId === orderItem?.ItemId);
                itemNames += menuItem ? `${this._localizationService.GetName(menuItem.Names)}<br>` : "";
            }
            dialogMessage = this._localizationService
                .names("CART_ITEMS_PARTIAL_UNAVAILABLE_STORE_SWITCH")
                .replace("{{item_names}}", itemNames);
        }

        new Dialog(dialogMessage, [
            {
                label: this._localizationService.names("No"),
                value: "no",
                action: () => {
                    this._options?.onContinue?.(this._item);
                },
            },
            {
                label: this._localizationService.names("Yes"),
                value: "yes",
                action: () => { this._removeItemsAndChangeStoreAsync(invalidRecIds, tempAddress); },
            },
        ]);
    }


    private _startOrderContinued(tempAddress: ITempAddress | null): void {
        if (tempAddress)
            this._orderService.order.setAddress(tempAddress);

        // Set order Type Id
        this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, this._item.orderTypeId, this._profileService.loyaltyProvider);

        // The order of these matters - Menu redirects on no store
        this._eventBus.emit(EventTypes.STORE_CHANGED, this._item.selectedStore, this._profileService.loyaltyProvider, true); //true == isCacheTrigger, used for localstorage

        if (this._options?.onContinue) {
            const msg = new TimedMessage(this._localizationService.names("NEW_ADDRESS_UPDATED_SUCCESSFULLY").replace("{}", this._getOrderTypeName(this._item.orderTypeId)), undefined, 1500);
            msg.show();
            this._options?.onContinue(this._item);
        } else if (this._options?.rewardToRedeem)
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.REWARDS, this._options?.rewardToRedeem.rewardId, this._options?.rewardToRedeem.couponCode);
        else
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
    }

    private _getOrderTypeName(orderTypeId: number): string {
        switch (orderTypeId) {
            case OrderType.TAKE_OUT:
                return this._localizationService.names("TakeOut");
            case OrderType.DINE_IN:
                return this._localizationService.names("DineIn");
            case OrderType.DELIVERY:
                return this._localizationService.names("Delivery");
            default:
                return "Unknown";
        }
    }

    private readonly _removeItemsAndChangeStoreAsync = async (invalidRecIds: number[], tempAddress: ITempAddress | null): Promise<void> => {
        const orderItems = this._orderService.order.getProperty("Items");
        const uniqueItemRecIds = new Set(orderItems.map(x => x.ItemRecId));
        const isAllItems = invalidRecIds.length === uniqueItemRecIds.size;

        for (const recId of invalidRecIds) {
            const itemKey = orderItems.find(x => x.ItemRecId === recId)?.ItemKey || null;
            if (itemKey) {
                await this._orderService.order.deleteItemByItemKeyAsync(itemKey, this._profileService.loyaltyProvider);
                this._orderService.order.removeItemByItemRecId(recId);
            }
        }

        if (!this._options?.onContinue) {
            const name = isAllItems ? "CART_ITEMS_REMOVED_NOTICE" : "CART_UPDATED_NOTICE";
            const msg = new TimedMessage(this._localizationService.names(name), undefined, 1000);
            msg.show();
        }

        this._startOrderContinued(tempAddress);
    }

    /**
     * Updates the user's current position, calculates distances to stores, and lists stores.
     * Loads the map if it hasn't been initialized.
     * 
     * @param latitude - The user's latitude.
     * @param longitude - The user's longitude.
     */
    private readonly _updateUserPositionAndStoreList = async (latitude: number, longitude: number): Promise<void> => {
        this._item.currentLatitude = latitude;
        this._item.currentLongitude = longitude;
        await this._calculateStoreDistancesAsync();

        await this._renderStoreListAsync();

        // Load Map Provider if not already initialized
        if (!this._item.mapInstance)
            await this._initializeMap();
    }

    /**
     * Initializes and loads the Map with store locations, markers, clusters, and search functionality.
     */
    private readonly _initializeMap = async (): Promise<void> => {
        // Initialize the map
        await this._initializeMapAsync();

        this._createMapMarkersAndMarkerClusterAsync();

        // Set up the map's search box for finding locations
        this._initializeSearchBox();
    }

    /**
     * Initializes the Map and sets up default map settings.
     */
    private readonly _initializeMapAsync = async (): Promise<void> => {
        const mapDiv = document.getElementById("div_locations_map");
        if (!mapDiv)
            return;

        this._item.mapInstance = await this._modules.mapProvider.createMap(
            mapDiv,
            {
                zoom: this._context.getProperty("locationPermissionDenied") ? 4 : this._item.defaultMapZoom,
                center: { lat: 0, lng: 0 },
                disableDefaultUI: true,
                gestureHandling: "greedy",
                mapId: "Stores_Location_Map"
            }
        );

        // Add listener to update visible locations when map becomes idle
        this._item.mapInstance.onIdle(() => this._mapShowLocations());
    };

    private readonly _createMapMarkersAndMarkerClusterAsync = async (refreshMarkers: boolean = true): Promise<void> => {
        if (!this._item.mapInstance) return;

        this._item.mapInstance.resize();

        if (refreshMarkers)
            await this._createStoreMarkers();

        // cluster using marker handles
        this._item.mapInstance.setClusterMarkers?.(this._item.markers.map(x => x.marker));

        this._item.mapInstance.resize();
    }

    /**
     * Creates markers for each store location and attaches them to the map.
     */
    private readonly _createStoreMarkers = async (): Promise<void> => {
        if (!this._item.mapInstance) return;

        const languageCode = this._context.getProperty("languageCode");

        this._item.mapInstance.clearMarkers();
        this._item.markers = [];

        let storeIndex = 0;
        for (const store of this._data.locations) {
            storeIndex++;
            const orderTypeOffered = store.orderTypes.includes(this._item.orderTypeId);

            const coords = { lat: store.latitude, lng: store.longitude };

            const contentFolderUrl = this._context.getProperty("publicStorageUrl");
            const url = orderTypeOffered
                ? `${contentFolderUrl}/online3/svg/location_pinpoint.svg`
                : `${contentFolderUrl}/online3/svg/location_pinpoint_disabled.svg`;

            const marker = await this._item.mapInstance.addMarkerAsync(coords, { iconUrl: url });

            marker.onClick(() => {
                const shouldShowStartOrderButton = this._item.orderTypeId !== OrderType.DELIVERY && !this._options?.forSettingPreferredStore;
                const html = this._modules.mapMarkerInfoWindow.createStoreInfoWindow(store, languageCode, shouldShowStartOrderButton ? () => this._infoWindowStartOrder(store.storeKey) : undefined);
                this._item.mapInstance!.openInfo(marker, html);
                this._selectStoreFromMarker(store);
            });

            // keep your own metadata mapping storeKey -> marker
            this._item.markers.push({
                storeKey: store.storeKey,
                coords,
                marker
            });
        }
    }

    private readonly _infoWindowStartOrder = (storeKey: string): void => {
        this._resetStoreSelection();
        this._item.selectedStore = storeKey;
        this._startOrderOnClickAsync();
    }

    private readonly _selectStoreFromMarker = (store: IStoreLocation): void => {
        if (this._item.orderTypeId === OrderType.DELIVERY) return;

        let storeElementId = `div_locations_store_${store.storeKey}`;

        if (this._selectedStoreList === StoreListType.FAVORITES) 
            storeElementId = `div_locations_store_favorites_${store.storeKey}`;

        const storeElement = document.getElementById(storeElementId);

        if (storeElement) {
            const parent = storeElement.parentElement;
            if (parent) {
                parent.scrollTo({ top: storeElement.offsetTop - parent.offsetTop, behavior: "smooth" });
            }
            this._handleStoreClick(store, storeElement);
        }
    }

    /**
     * Initializes the search box to allow users to search for specific locations.
     * Adjusts the map to show the search result and re-calculates distances.
     */
    private _initializeSearchBox(): void {
        const input = document.getElementById("txt_locations_search_box") as HTMLInputElement | null;
        const chain = this._modules.geoSearch?.chain;
        if (!input || !this._item.mapInstance || !chain?.hasSuggestProvider) return;

        this._modules.addressSuggestBox?.dispose();
        this._modules.addressSuggestBox = new AddressSuggestBox({
            input,
            chain,
            mode: "locality",
            getBiasLocation: () => this._getMapCenter(),
            getCountryCodes: () => (this._data.getBrandProperty("Countries") ?? []).map(country => country.IsoCode),
            getAnnouncement: () => this._localizationService.names("AutoSuggestionsAvailable"),
            onAddressSelected: async ({ coords }) => {
                this._item.currentLatitude = coords.lat;
                this._item.currentLongitude = coords.lng;

                await this._calculateStoreDistancesAsync();

                // recenter/zoom in a provider-neutral way
                this._item.mapInstance!.setCenter(coords);
                this._item.mapInstance!.setZoom(this._item.defaultMapZoom);
            }
        });
    };

    private _getMapCenter(): LatLng | null {
        const bounds = this._item.mapInstance?.getBoundsRect?.();
        if (!bounds) return null;

        return {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2
        };
    }

    /**
     * Displays store locations within the current map bounds. Hides locations outside the bounds.
     * If no stores are visible, zooms out the map to include stores. 
     */
    private _mapShowLocations(): void {
        if (!this._item.mapInstance)
            return;

        let visibleStoreCount = 0;

        // Show or hide markers based on their presence within the map bounds 
        for (const marker of this._item.markers) {
            const isWithinView = this._item.mapInstance.isInView(marker.coords);
            const storeElementId = `div_locations_store_${marker.storeKey}`;

            if (isWithinView) {
                Util.showElement(storeElementId);
                visibleStoreCount++;
            } else {
                Util.hideElement(storeElementId);

                if (this._item.selectedStore == marker.storeKey) {
                    this._item.selectedStore = "";
                    Util.setElementClass("remove", storeElementId, "active");
                }
            }
        }

        // If no stores are visible, zoom out to expand the visible area 
        this._adjustZoomIfNoStoresVisible(visibleStoreCount);
    }

    /**
     * Adjusts the map zoom level if no stores are visible within the current bounds.
     * @param visibleStoreCount - The count of stores currently visible within map bounds. 
     */
    private _adjustZoomIfNoStoresVisible(visibleStoreCount: number): void {
        if (visibleStoreCount !== 0 || !this._item.mapInstance)
            return;

        const currentZoom = this._item.mapInstance.getZoom() || 2;
        const minZoomLevel = 4;

        if (currentZoom > minZoomLevel)
            this._item.mapInstance.setZoom(currentZoom - 2);
    }

    /**
     * Updates the distances for each location, sorts locations by distance,
     * and updates relevant UI elements and markers.
     */
    private readonly _calculateStoreDistancesAsync = async (): Promise<void> => {
        // Check if current latitude and longitude are set
        if (this._item.currentLatitude === 0 || this._item.currentLongitude === 0)
            return;

        // Calculate and update distance for each location
        for (const location of this._data.locations) {
            location.distance = Util.Float2(
                Util.GetDistance(this._item.currentLatitude, this._item.currentLongitude, location.latitude, location.longitude, "M")
            );
        }

        // Sort locations by distance
        this._data.locations.sort((a: IStoreLocation, b: IStoreLocation) => a.distance - b.distance);

        await this._renderStoreListAsync();
    }

    /**
     * Renders the list of stores based on the user's favorite stores.
     * Updates the UI by attaching store cards to the store list container and adding event listeners.
     * 
     * @param favorites - Array of favorite stores with store keys and names.
     */
    private readonly _renderStoreListAsync = async (): Promise<void> => {
        const storeListContainer = document.getElementById("div_locations_store_list_cards");
        if (!storeListContainer)
            return;

        storeListContainer.replaceChildren();

        const fragment = document.createDocumentFragment();

        // Generate and append store cards to the container
        const stores = await this._generateStoreCards();
        for (const store of stores) {
            fragment.appendChild(store);
        }

        // Append all at once
        storeListContainer.appendChild(fragment);
    }

    private readonly _clearStoreListContainer = (): void => {
        const storeListContainer = document.getElementById("div_locations_store_list_cards");
        if (!storeListContainer)
            return;

        storeListContainer.innerHTML = "";
    }

    private readonly _renderFavoriteStoreList = (): boolean => {
        const storeFavoriteListContainer = document.getElementById("div_locations_store_favorites_cards");
        if (!storeFavoriteListContainer)
            return false;

        storeFavoriteListContainer.replaceChildren();

        const fragment = document.createDocumentFragment();

        // Generate and append store cards to the container
        const stores = this._generateFavoriteStoreCards();
        for (const store of stores) {
            fragment.appendChild(store);
        }

        // Append all at once
        storeFavoriteListContainer.appendChild(fragment);

        return stores.length > 0;
    }

    /**
     * 
     * Generates a list of store cards (HTML elements) based on store data and favorites.
     * Sorts stores by favorite status and distance from the user's location.
     *
     * @param favorites - An array of favorite stores with store keys and names, or null if no favorites.
     * @returns An array of HTML elements representing the store cards.
     */
    private readonly _generateStoreCards = async (): Promise<HTMLElement[]> => {

        // Retrieve the list of stores, optionally filtered for Bahamas
        const isBahamas = Util.isBahamas(this._item.currentLongitude, this._item.currentLatitude) ? "HARDCODEDBH" : null;
        const storeList = this._filterStores(isBahamas);

        if (storeList.length === 0) return [];

        return storeList.map((store, index) =>
            this._createStoreCardElement(store, index + 1, false)
        ) ?? [];
    }

    private readonly _generateFavoriteStoreCards = (): HTMLElement[] => {
        const storeCards: HTMLElement[] = [];
        const isBahamas = Util.isBahamas(this._item.currentLongitude, this._item.currentLatitude) ? "HARDCODEDBH" : null;

        // Retrieve the list of stores, optionally filtered for Bahamas
        const storeKeys = this._profileService.user.getProperty("FavoriteStores") || [];
        let storeList = this._filterStores(isBahamas);
        storeList = storeList.filter(x => storeKeys.includes(x.storeKey));

        // Generate store cards for open stores only
        let storeIndex = 0;
        for (const store of storeList) {
            storeIndex++;
            storeCards.push(this._createStoreCardElement(store, storeIndex, true));
        }

        return storeCards;
    }

    /**
     * Filters the list of stores based on a search term or location-specific criteria.
     * If searching for Bahamas, only returns stores in Bahamas; otherwise, filters by search terms.
     *
     * @param search - A search string or a special code (e.g., "HARDCODEDBH" for Bahamas filtering).
     * @returns An array of locations matching the search criteria.
     */
    private readonly _filterStores = (search: string | null): IStoreLocation[] => {
        // Filter specifically for stores located in Bahamas
        if (search === 'HARDCODEDBH')
            return this._data.locations.filter(store => store.state === "BH");

        // If no search term is provided, return all locations
        if (!search)
            return this._data.locations;

        // Filter locations based on multiple search terms
        const searchList = search.toLowerCase().split(' ');
        return this._data.locations.filter(location => {
            return (
                searchList.every(term =>
                    location.address1.toLowerCase().includes(term) ||
                    location.address2?.toLowerCase().includes(term) ||
                    location.name.toLowerCase().includes(term) ||
                    location.city.toLowerCase().includes(term) ||
                    location.state.toLowerCase().includes(term) ||
                    location.fullState?.toLowerCase().includes(term) ||
                    location.zip.toLowerCase().includes(term)
                )
            );
        });
    }

    /**
     * Creates an HTML element for displaying a store card with status, address, and distance.
     * @param {IStoreLocation} store - The store location data.
     * @param {number} index - The index of the store in the list, used for marker numbering.
     * @returns {HTMLElement} The HTML element representing the store card.
     */
    private readonly _createStoreCardElement = (store: IStoreLocation, index: number, isFavorite: boolean): HTMLElement => {
        const orderTypeOffered = store.orderTypes.includes(this._item.orderTypeId);
        const orderType = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === this._item.orderTypeId);
        const orderTypeName = orderType ? this._localizationService.GetName(orderType.Names) : "Undefined";

        const storeStatusHtml = this._getStoreStatusHtml(store.statusId);
        const numberedIconHtml = this._getNumberedIconHtml(index, store.storeKey);
        const addressObject = this._formatAddress(store);
        const distanceSpanHtml = this._getDistanceHtml(store.distance, store.storeKey);
        const hoursInfoHtml = this._getHoursHtml(store.businessStartMinute, store.hours, store.storeKey);
        const favoriteHtml = this._getFavoriteSectionHtml(store.storeKey, isFavorite);

        const id = isFavorite ? `div_locations_store_favorites_${store.storeKey}` : `div_locations_store_${store.storeKey}`;
        const errorId = `store_error_${store.storeKey}`;
        const enabled = orderTypeOffered && (store.statusId === StoreStatusId.ACTIVE);

        const storeItemHtml = `
            <div id="${id}" class="store-address" ${enabled ? "" : "disabled"} tabindex="0" aria-describedby="${errorId}">
                <div>
                    ${numberedIconHtml}
                    <div class="store-section">
                        <h3 class="store-name">${addressObject.address1}</h3>
                        ${addressObject.address3 ? `<p>${addressObject.address3}</p>` : ""}
                        <p>${addressObject.cityState}</p>
                        <p>${distanceSpanHtml} <span aria-hidden="true">&#8226;</span> ${hoursInfoHtml}</p>
                        ${storeStatusHtml}
                    </div>
                    ${favoriteHtml}
                </div>
                ${orderTypeOffered ? "" : `<p id="${errorId}" class="store-error">${this._localizationService.names("OrderTypeNotOfferedAtThisLocation").replace("{{order_type}}", orderTypeName)}</p>`}
            </div>`;

        const div = Util.createHtmlElementFromTemplate(storeItemHtml);
        div.onclick = () => this._handleStoreClick(store, div);

        const favoriteId = isFavorite ? `div_locations_store_favorites_favorite_${store.storeKey}` : `div_locations_store_favorite_${store.storeKey}`;
        const favoriteHeart = div.querySelector(`#${favoriteId}`) as HTMLElement;
        if (favoriteHeart) {
            favoriteHeart.onclick = (event) => {
                event.stopPropagation();
                this._favoriteHeartOnClickAsync(store.storeKey);
            };

            Util.addKeyboardActivation(favoriteHeart, (event) => {
                event.stopPropagation();
                this._favoriteHeartOnClickAsync(store.storeKey);
            });
        }

        const startOrderButtonContainer = div.firstElementChild as HTMLDivElement;
        const startOrderButton = this._createStoreCardStartOrderButton(store, div);
        startOrderButtonContainer?.appendChild(startOrderButton);

        return div;
    }

    private readonly _createStoreCardStartOrderButton = (store: IStoreLocation, div: HTMLElement): HTMLButtonElement => {
        const button = document.createElement("button");

        if (this._options?.isSave) {
            button.textContent = this._localizationService.names("SelectStore");
            button.setAttribute("ltag", "SelectStore");
        }
        else {
            button.textContent = this._localizationService.names("StartOrder");
            button.setAttribute("ltag", "StartOrder");
        }

        button.onclick = (event) => {
            event.stopPropagation();
            this._handleStoreClick(store, div);
            this._startOrderOnClickAsync();
        };
        return button;
    };

    /**
     * Generates the HTML for displaying the store status based on its status ID.
     * @param {StoreStatusId} statusId - The store status ID.
     * @returns {string} HTML representing the store status.
     */
    private readonly _getStoreStatusHtml = (statusId: StoreStatusId): string => {
        const statusMap = {
            [StoreStatusId.ACTIVE]: "",
            [StoreStatusId.TEMPORARILY_CLOSED]: "TemporarilyClosed",
            [StoreStatusId.CLOSED]: "",
            [StoreStatusId.INACTIVE]: "Inactive",
            [StoreStatusId.COMING_SOON]: "ComingSoon",
        };
        const statusKey = statusMap[statusId];
        return statusKey ? `<h3 ltag="${statusKey}">${this._localizationService.names(statusKey)}</h3>` : "";
    };

    /**
     * Generates the numbered icon HTML based on index and store key.
     * @param {number} index - The index of the store.
     * @param {string} storeKey - The unique key of the store.
     * @returns {string} HTML representing the numbered icon or an empty string if using SVG icons.
     */
    private readonly _getNumberedIconHtml = (index: number, storeKey: string): string => {
        if (this._item.useSvgIcon)
            return "";

        const content = index > 99 ? '99+' : index;

        //<div class="icon-pin" style="width: 26px; height: 38px; fill: var(--btnColorBg);"></div>
        return `
        <div class="location-store-marker">
            <svg xmlns="http://www.w3.org/2000/svg" width="26px" height="38px" viewBox="0 0 26 37" fill="var(--btnColorBg)"><g><path d="M13 0C5.8175 0 0 5.77328 0 12.9181C0 20.5733 5.59 23.444 9.55499 30.0784C12.09 34.3207 11.3425 37 13 37C14.7225 37 13.975 34.2569 16.445 30.1422C20.085 23.8586 26 20.6052 26 12.9181C26 5.77328 20.1825 0 13 0Z"></path><path d="M13.0167 35C12.7836 35 12.7171 34.9346 12.3176 33.725C11.9848 32.6789 11.4854 31.0769 10.1873 29.1154C8.92233 27.1866 7.59085 25.6173 6.32594 24.1135C3.36339 20.5174 1 17.7057 1 12.6385C1.03329 6.19808 6.39251 1 13.0167 1C19.6408 1 25 6.23078 25 12.6385C25 17.7057 22.6699 20.55 19.6741 24.1462C18.4425 25.65 17.1443 27.2193 15.8793 29.1154C14.6144 31.0442 14.0818 32.6135 13.749 33.6596C13.3495 34.9346 13.2497 35 13.0167 35Z"></path></g></svg>
            <div id="div_locations_store_index_${storeKey}" class="location-store-marker-content">${content}</div>
        </div>`;
    };

    private readonly _getFavoriteSectionHtml = (storeKey: string, isFavorite: boolean): string => {
        if (!this._profileService.user.isLoggedIn() || this._options?.forSettingPreferredStore)// || loyaltyProvider.getLoyaltyProvider() === LoyaltyProvider.PUNCHH)
            return "";

        const isFavorited = this._profileService.user.getProperty("FavoriteStores")?.includes(storeKey) || false;
        const favoriteId = isFavorite ? `div_locations_store_favorites_favorite_${storeKey}` : `div_locations_store_favorite_${storeKey}`;

        return `
            <div id="${favoriteId}" class="favorite-section" role="button" tabindex="0" aria-pressed="${isFavorited}" aria-label="${isFavorited ? this._localizationService.names("RemoveFromFavorites") : this._localizationService.names("AddToFavorites")}">
                <span id="spn_locations_store_favorite_${storeKey}" class="${isFavorited ? "icon-heart-filled" : "icon-favorites"}"></span>
            </div>`;
    };

    /**
     * Formats the store's address for display.
     * @param {IStoreLocation} store - The store location data.
     * @returns {IAddressFormat} The formatted address.
     */
    private readonly _formatAddress = (store: IStoreLocation): { address1: string, address2: string, address3: string, cityState: string, country: string } => {
        return Util.formatAddressObject({
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        }, this._data.getBrandProperty("Countries"));
    };

    /**
     * Creates HTML for displaying the store distance.
     * @param {number} distance - The distance to the store.
     * @param {string} storeKey - The unique key of the store.
     * @returns {string} HTML representing the store distance.
     */
    private readonly _getDistanceHtml = (distance: number, storeKey: string): string => {
        const distanceText = this._createDistanceText(distance);
        return `<span id="spn_locations_store_distance_${storeKey}" ltagj="${distanceText.ltagj}">${distanceText.text}</span>`;
    };

    /**
     * Generates the HTML for displaying the store hours information.
     * @param {IStoreLocationHour[]} hours - The store's operating hours.
     * @param {string} storeKey - The unique key of the store.
     * @returns {string} HTML representing the store hours information.
     */
    private readonly _getHoursHtml = (businessStartMinute: number, hours: IStoreLocationHour[], storeKey: string): string => {
        const hoursInfo = this._createHoursText(businessStartMinute, hours);
        return `<span id="spn_locations_store_status_${storeKey}" ltagj="${hoursInfo.ltagj}">${hoursInfo.text}</span>`;
    };

    /**
     * Handles the click event for a store element, setting it as active and centering on the map.
     * @param {IStoreLocation} store - The store location data.
     * @param {HTMLElement} div - The store element that was clicked.
     */
    private readonly _handleStoreClick = (store: IStoreLocation, div: HTMLElement): void => {
        this._resetStoreSelection();

        this._item.selectedStore = store.storeKey;
        div.classList.add("active");

        this._showStoreOnMap(store);
    };

    private readonly _resetStoreSelection = (): void => {
        const storeCards = Array.from(document.querySelectorAll(".store-address"));
        for (const div of storeCards) {
            div.classList.remove("active");
        }
        this._deliveryAddressChanged(AddressValidation.INVALID, "", null);
    };

    private readonly _moveToStoreLocationOnMap = (store: IStoreLocation): void => {
        const location: LatLng = { lat: store.latitude, lng: store.longitude };

        if (this._item.mapInstance) {
            this._item.mapInstance.setCenter(location);
            this._item.mapInstance.setZoom(this._item.defaultMapZoom);
        }
    }

    private _showStoreOnMap(store: IStoreLocation): void {
        const markerInfo = this._item.markers.find((m) => m.storeKey === store.storeKey);

        if (markerInfo) {
            this._moveToStoreLocationOnMap(store);
            this._showStoreInfo(markerInfo.marker, store);
        }
    }

    private _showStoreInfo(marker: IMapMarker, storeLocation: IStoreLocation): void {
        const languageCode = this._context.getProperty("languageCode");
        const shouldShowStartOrderButton = this._item.orderTypeId !== OrderType.DELIVERY && !this._options?.forSettingPreferredStore;
        const infoHtml = this._modules.mapMarkerInfoWindow.createStoreInfoWindow(
            storeLocation,
            languageCode,
            shouldShowStartOrderButton ? () => this._infoWindowStartOrder(storeLocation.storeKey) : undefined,
        );
        this._item.mapInstance?.openInfo(marker, infoHtml);
    }

    /**
     * Creates a formatted text for store distance based on units of measurement and localizations.
     * @param {number} distance - The distance to the store.
     * @returns {{ text: string, ltagj: string }} An object containing the localized distance text and its language tag (ltagj).
     */
    private readonly _createDistanceText = (distance: number): { text: string, ltagj: string } => {
        const countryId = this._data.getBrandProperty("StoreInfo").countryId
        const isMetric = this._data.getBrandProperty("Countries").find(c => c.CountryID == countryId)?.UnitOfMeasurement === "Metric";
        const formattedDistance = isMetric ? Util.Float2(this._convertMilesToKilometers(distance)) : distance;
        const units = this._getDistanceUnits(formattedDistance, isMetric);

        const languages = this._localizationService.getSupportedLanguages();
        const nameObj: IName[] = languages.map(lang => {
            const tempText = this._localizationService.names("StoreDistanceAway", lang)
                .replace("{distance}", `${formattedDistance}`)
                .replace("{type}", this._localizationService.names(units, lang));
            return { CULT: lang, NAM: tempText };
        });

        const text = this._localizationService.names("StoreDistanceAway")
            .replace("{distance}", `${formattedDistance}`)
            .replace("{type}", this._localizationService.names(units));
        return { text, ltagj: Util.toLtagj(nameObj) };
    }

    /**
    * Converts miles to kilometers
    * 
    * @param {Number} miles - number of miles to convert to kilometers.
    * @returns {Number} - The number of kilometers.
    */
    private _convertMilesToKilometers(miles: number): number {
        const conversionFactor = 1.60934;
        return (miles * conversionFactor);
    }

    /**
     * Determines the appropriate unit label for the distance based on measurement system.
     * @param {number} distance - The distance value.
     * @param {boolean} isMetric - Whether the metric system is used.
     * @returns {string} The unit label ("mile", "miles", "kilometer", "kilometers").
     */
    private readonly _getDistanceUnits = (distance: number, isMetric: boolean): string => {
        if (isMetric)
            return distance === 1 ? "kilometer" : "kilometers";

        return distance === 1 ? "mile" : "miles";
    };

    /**
     * Creates a formatted text for store hours based on current time and store hours.
     * @param {IStoreLocationHour[]} hours - The store's operating hours.
     * @returns {{ text: string, ltagj: string }} An object containing the localized hours text and its language tag (ltagj).
     */
    private readonly _createHoursText = (businessStartMinute: number, hours: IStoreLocationHour[]): { text: string, ltagj: string } => {
        const { text, time, weekday } = this._getHoursInfo(businessStartMinute, hours);

        const languages = this._localizationService.getSupportedLanguages();
        const nameObj: IName[] = languages.map(lang => {
            const tempText = this._localizationService.names(text, lang)
                .replace("{time}", time)
                .replace("{weekday}", this._localizationService.names(weekday, lang));
            return { CULT: lang, NAM: tempText };
        });

        const displayText = this._localizationService.names(text)
            .replace("{time}", time)
            .replace("{weekday}", this._localizationService.names(weekday));
        return { text: displayText, ltagj: Util.toLtagj(nameObj) };
    }

    /**
     * Determines the store's current status (open, closed, or opening soon) and provides the relevant text.
     * @param {IStoreLocationHour[]} hours - The store's operating hours.
     * @returns {{ text: string, time: string, weekday: string }} An object with text, time, and weekday for the store's hours status.
     */
    private readonly _getHoursInfo = (businessStartMinute: number, hours: IStoreLocationHour[]): { text: string, time: string, weekday: string } => {
        const currentDateTime = Util.getBusinessDateManual(this._data.getBrandProperty("TimeOffset"), businessStartMinute); 
        const currentDay = currentDateTime.getDay();
        const currentTimeInMinutes = Util.getNowStoreMinutesManual(0, businessStartMinute);

        const todayHours = hours.find(h => h.weekdayId === currentDateTime.getDay());

        if (todayHours) {
            if (currentTimeInMinutes < todayHours.startMinute) {
                // Store will open later today
                return { text: "OpensAt", time: Util.formatTimeByIsoCode(todayHours.startMinute, this._context.getProperty("languageCode")), weekday: "" };
            } else if (currentTimeInMinutes < todayHours.endMinute) {
                // Store is currently open
                return { text: "OpenUntil", time: Util.formatTimeByIsoCode(todayHours.endMinute, this._context.getProperty("languageCode")), weekday: "" };
            }
        }

        // Store is closed, find the next opening day
        for (let i = 1; i <= 7; i++) {
            const nextDay = (currentDay + i) % 7;
            const nextDayHours = hours.find(h => h.weekdayId === nextDay);
            if (nextDayHours) {
                return {
                    text: "ClosedOpensAt",
                    time: Util.formatTimeByIsoCode(nextDayHours.startMinute, this._context.getProperty("languageCode")),
                    weekday: Util.getWeekdayNameById(nextDay)
                };
            }
        }

        return { text: "Closed", time: "", weekday: "" };
    };

    /**
     * Centers the map on the user's current location.
     * If the user's location is not set, it defaults to a central position.
     */
    private readonly _centerMapOnUser = (): void => {
        if (!this._item.mapInstance)
            return;

        const defaultLocation = this._getDefaultUserCoordinates();
        const userLocation: LatLng = {
            lat: this._item.currentLatitude || defaultLocation.lat,
            lng: this._item.currentLongitude || defaultLocation.lng
        };

        this._item.mapInstance.setCenter(userLocation);
    }

    private readonly _getDefaultUserCoordinates = (country?: string): LatLng => {
        return getCountryCentroid(country ?? this._getBrandCountryCode());
    }

    private readonly _getBrandCountryCode = (): string | undefined => {
        return this._data.getBrandProperty("StoreInfo")?.country?.IsoCode;
    }

    private readonly _favoriteHeartOnClickAsync = async (storeKey: string): Promise<void> => {
        const isFavorited = this._profileService.user.getProperty("FavoriteStores")?.includes(storeKey) || false;
        if (isFavorited)
            await this._deleteFavoriteStore(storeKey);
        else
            await this._saveFavoriteStore(storeKey);

        this._renderFavoriteStoreList();
    }

    /**
     * Keeps the favorite heart's accessible state (aria-label/aria-pressed) in sync
     * with its visual state, for both the main store list and favorites list cards.
     * @param {string} storeKey - The unique key for the store.
     * @param {boolean} isFavorited - Whether the store is currently favorited.
     */
    private readonly _updateFavoriteAriaState = (storeKey: string, isFavorited: boolean): void => {
        const ariaLabel = isFavorited ? this._localizationService.names("RemoveFromFavorites") : this._localizationService.names("AddToFavorites");
        const ids = [`div_locations_store_favorite_${storeKey}`, `div_locations_store_favorites_favorite_${storeKey}`];
        for (const id of ids) {
            const element = document.getElementById(id);
            if (element) {
                element.setAttribute("aria-pressed", String(isFavorited));
                element.setAttribute("aria-label", ariaLabel);
            }
        }
    }

    private readonly _saveFavoriteStore = async (storeKey: string): Promise<void> => {
        const result = await this._profileService.saveFavoriteStore(storeKey);
        if (result) {
            Util.setElementClass("add", `spn_locations_store_favorite_${storeKey}`, "icon-heart-filled");
            Util.setElementClass("remove", `spn_locations_store_favorite_${storeKey}`, "icon-favorites");
            this._updateFavoriteAriaState(storeKey, true);
            this._eventBus.emit(EventTypes.FAV_STORE_ADD, storeKey);
        } else
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("favoriteStoreSaveName_Error"),
                true
            );

        if (this._dataLayer) {
            const storeCode = this._data.locations.find(x => x.storeKey === storeKey)?.storeCode || '';
            this._dataLayer.favorite(ContentType.LOCATION, storeCode);
        }
    }

    /**
     * Deletes a store from the user's favorites.
     * @private
     * @async
     * @param {string} storeKey - The unique key for the store to delete.
     * @returns {Promise<void>} A promise that resolves with no value.
     */
    private readonly _deleteFavoriteStore = async (storeKey: string): Promise<void> => {
        const result = await this._profileService.deleteFavoriteStore(storeKey) || false;
        if (result) {
            Util.setElementClass("add", `spn_locations_store_favorite_${storeKey}`, "icon-favorites");
            Util.setElementClass("remove", `spn_locations_store_favorite_${storeKey}`, "icon-heart-filled");
            this._updateFavoriteAriaState(storeKey, false);
            this._eventBus.emit(EventTypes.FAV_STORE_REMOVE, { storeKey, isFavoritesPage: false });
        } else
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("favoriteStoreDelete_Error"),
                true
            );

        if (this._dataLayer) {
            const storeCode = this._data.locations.find(x => x.storeKey === storeKey)?.storeCode || '';
            this._dataLayer.remove_favorite(ContentType.LOCATION, storeCode);
        }
    }

    /**
     * handles case where user unfavorites store from favorites page, leading to UI inconsistency
     */
    private readonly _removeFavoriteStoreHandler = (storeKey: string, isFavoritesPage: boolean): void => {
        if (!isFavoritesPage) return;

        Util.setElementClass("add", `spn_locations_store_favorite_${storeKey}`, "icon-favorites");
        Util.setElementClass("remove", `spn_locations_store_favorite_${storeKey}`, "icon-heart-filled");
        this._updateFavoriteAriaState(storeKey, false);
        this._renderFavoriteStoreList();
    }

    private readonly _startOrder = async (storeKey: string): Promise<void> => {
        // 2DO: Create new url for menu

        let url = "";
        //if (localAolo.Temp.BrandOfferCode)
        //    url = await this._getCampaignUrl(storeKey, localAolo);
        //else
        //    url = `${location.origin}/?id=${storeKey}`;

        //if (localAolo.specialCode)
        //    url += `&specialcode=${localAolo.specialCode}`;

        //if (localAolo.reOrderId) {
        //    url += `&reorder=${localAolo.reOrderId}`;
        //    delete localAolo.reOrderId;
        //}

        //if (localAolo.orderCouponId) {
        //    url += `&ordercoupon=${localAolo.orderCouponId}`;
        //    delete localAolo.orderCouponId;
        //}

        //if (Util.isAppView()) {
        //    url += "&mobile=true";
        //    const tempLoct = this._locations.find(l => l.storeKey === storeKey);
        //    if (tempLoct) {
        //        if (tempLoct.distance > 25) {
        //            let customerDistance = tempLoct.distance;
        //            let messageName = "XtraMiles";
        //            if (true) {
        //                customerDistance = Util.Float2(Util.convertMilesToKilometers(customerDistance));
        //                messageName = "XtraKilometers";
        //            }
        //            DialogCreators.messageBox(Names(messageName).replace("??", `<div class='warning bold inline'>${customerDistance}</div>`), localAolo.buttonHoverStyle, [
        //                { "text": Names("YesProceed"), "callBack": () => { this._yesCallBack(url); } },
        //                { "text": Names("NoOtherStore"), "callBack": this._noCallBack }]);
        //        } else {
        //            this._yesCallBack(url)
        //        }
        //    }
        //} else
        //    window.open(url, '_blank');
    }

    private readonly _deleteAddress = async (e: Event) => {
        e.stopPropagation()
        const elementDelete = e.target as HTMLElement;
        const user = this._profileService.user.user;
        const addressId = Number(elementDelete.getAttribute("data-address"));
        const index = user.Addresses?.findIndex(a => a.AID == addressId);

        if (index == undefined)
            return;

        const dialog = new Dialog(
            this._localizationService.names("CONFIRM_DELETE_ADDRESS").replace("{{address}}", `${user.Addresses?.[index].STRNO} ${user.Addresses?.[index].ADDR1}`),
            [
                { label: this._localizationService.names("No"), value: "no" },
                { label: this._localizationService.names("Yes"), value: "yes", action: () => this._acceptDeleteAddressAsync(elementDelete, addressId) }
            ]
        );
    }

    private readonly _acceptDeleteAddressAsync = async (element: HTMLElement, addressId: number): Promise<void> => {
        const success = await this._profileService.deleteDeliveryAddress(addressId)

        if (success) {
            element.parentElement?.remove();

            await this._profileService.signInJWT();
            this._setOptions();

            const selectedAddressId = this._modules.deliveryAddress?.getDeliveryAddressInfo()?.address?.AddressID;

            if (selectedAddressId) {
                if (selectedAddressId === addressId)
                    this._deliveryAddressChanged(AddressValidation.INVALID, "", null);
                else
                    await this._deliveryBoxOnClickAsync(selectedAddressId);
            }
        }
    }
}